/**
 * Migration Service
 * 
 * Handles export/import of all data as a ZIP file including:
 * - All database tables (automatically detected from TABLES constant)
 * - All storage buckets (images, audio files)
 * 
 * This is generic and automatically includes new tables when added to TABLES.
 * 
 * Export format:
 * ambi-export-{date}.zip
 * ├── manifest.json          # Version, export date, checksums
 * ├── data.json              # All table data
 * └── files/
 *     ├── user-images/       # Image files
 *     ├── user-audio/        # Audio files  
 *     └── audio-files/       # Additional audio
 */

import JSZip from 'jszip'
import { logger } from '@/lib/logger'
import { TABLES, STORAGE_BUCKETS, type DataProvider, type FileMetadata } from './types'

// Export manifest for versioning and validation
export interface ExportManifest {
  version: number
  exportedAt: string
  appVersion: string
  tableCount: number
  fileCount: number
  tables: string[]
  buckets: string[]
}

// Progress callback for UI updates
export interface MigrationProgress {
  phase: 'tables' | 'files' | 'packaging' | 'extracting' | 'importing'
  current: number
  total: number
  currentItem?: string
}

export type ProgressCallback = (progress: MigrationProgress) => void

// Current export format version - increment when format changes
const EXPORT_VERSION = 1

/**
 * Export all data from the provider as a ZIP file
 */
export async function exportToZip(
  provider: DataProvider,
  onProgress?: ProgressCallback
): Promise<Blob> {
  const zip = new JSZip()
  const tableNames = Object.values(TABLES)
  const bucketNames = Object.values(STORAGE_BUCKETS)
  
  // Phase 1: Export all tables
  const tableData: Record<string, any[]> = {}
  let tableIndex = 0
  
  for (const table of tableNames) {
    onProgress?.({
      phase: 'tables',
      current: tableIndex,
      total: tableNames.length,
      currentItem: table
    })
    
    try {
      const data = await provider.query(table)
      tableData[table] = data
      logger.info(`[MigrationService] Exported ${data.length} records from ${table}`)
    } catch (error) {
      logger.warn(`[MigrationService] Failed to export table ${table}:`, error)
      tableData[table] = []
    }
    
    tableIndex++
  }
  
  // Add data.json to zip
  zip.file('data.json', JSON.stringify(tableData, null, 2))
  
  // Phase 2: Export all files from storage buckets
  let fileCount = 0
  const filesFolder = zip.folder('files')!
  
  for (const bucket of bucketNames) {
    const bucketFolder = filesFolder.folder(bucket)!
    
    onProgress?.({
      phase: 'files',
      current: fileCount,
      total: -1, // Unknown until we enumerate
      currentItem: `Scanning ${bucket}...`
    })
    
    try {
      // List all files in bucket root (user files are stored with userId prefix)
      const files = await listAllFiles(provider, bucket, '')
      
      for (const file of files) {
        onProgress?.({
          phase: 'files',
          current: fileCount,
          total: files.length,
          currentItem: file.path
        })
        
        // Get the actual file blob
        const blob = await getFileBlob(provider, bucket, file.path)
        if (blob) {
          // Store with sanitized path (replace slashes with safe separator)
          const safePath = file.path.replace(/\//g, '__')
          bucketFolder.file(safePath, blob)
          
          // Also store metadata
          bucketFolder.file(`${safePath}.meta.json`, JSON.stringify({
            originalPath: file.path,
            size: file.size,
            contentType: file.contentType,
            createdAt: file.createdAt,
            updatedAt: file.updatedAt
          }))
          
          fileCount++
        }
      }
      
      logger.info(`[MigrationService] Exported ${files.length} files from ${bucket}`)
    } catch (error) {
      logger.warn(`[MigrationService] Failed to export bucket ${bucket}:`, error)
    }
  }
  
  // Phase 3: Create manifest
  const manifest: ExportManifest = {
    version: EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    appVersion: '1.0.0', // TODO: Get from package.json
    tableCount: tableNames.length,
    fileCount,
    tables: tableNames,
    buckets: bucketNames
  }
  
  zip.file('manifest.json', JSON.stringify(manifest, null, 2))
  
  // Phase 4: Generate ZIP
  onProgress?.({
    phase: 'packaging',
    current: 0,
    total: 1,
    currentItem: 'Creating ZIP file...'
  })
  
  const zipBlob = await zip.generateAsync({ 
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  })
  
  logger.info(`[MigrationService] Export complete: ${tableNames.length} tables, ${fileCount} files, ${(zipBlob.size / 1024 / 1024).toFixed(2)} MB`)
  
  return zipBlob
}

/**
 * Import data from a ZIP file into the provider
 * 
 * Options:
 * - clearExisting: If true, clears all existing data before import
 * - preserveHueLights: If true, matches Hue lights by hardware ID to preserve references
 */
export interface ImportOptions {
  clearExisting?: boolean
  preserveHueLights?: boolean
  onProgress?: ProgressCallback
}

export interface ImportResult {
  success: boolean
  error?: string
  tablesImported: number
  recordsImported: number
  filesImported: number
  warnings: string[]
}

// Foreign key columns that need ID remapping during import
const FK_COLUMNS: Record<string, string[]> = {
  [TABLES.SCENES]: ['parent_id', 'container_id'],
  [TABLES.DISPLAYS]: [],
  [TABLES.SCENE_DISPLAY_CONFIGS]: ['scene_id', 'display_id'],
  [TABLES.DISPLAY_LAYERS]: ['scene_display_config_id', 'inherited_from_scene_id', 'overrides_layer_id'],
  [TABLES.DISPLAY_LAYER_OVERRIDES]: ['scene_display_config_id', 'parent_layer_id'],
  [TABLES.IMAGE_LIBRARY]: [],
  [TABLES.AUDIO_LIBRARY]: [],
  [TABLES.AUDIO_LIBRARY_FOLDERS]: ['parent_id'],
  [TABLES.AUDIO_LIBRARY_FILES]: ['folder_id'],
  [TABLES.MUSIC_NODES]: ['container_id', 'playlist_id'],
  [TABLES.PLAYLIST_NODES]: ['container_id'],
  [TABLES.SPOTIFY_NODES]: ['container_id'],
  [TABLES.SCENE_MUSIC_CONFIGS]: ['scene_id', 'music_node_id'],
  [TABLES.SCENE_PLAYLIST_CONFIGS]: ['scene_id', 'playlist_node_id'],
  [TABLES.SCENE_SPOTIFY_CONFIGS]: ['scene_id', 'spotify_node_id'],
  [TABLES.SCENE_LIGHT_CONFIGS]: ['scene_id', 'light_id'],
  [TABLES.HUE_BRIDGES]: [],
  [TABLES.LIGHTS]: ['bridge_id'],
  [TABLES.HUE_SYNC_SESSIONS]: ['bridge_id'],
  [TABLES.GRAPH_CONTAINERS]: [],
  [TABLES.SPOTIFY_INTEGRATIONS]: [],
  [TABLES.CHARACTERS]: [],
}

export async function importFromZip(
  provider: DataProvider,
  zipBlob: Blob,
  options: ImportOptions = {}
): Promise<ImportResult> {
  const { clearExisting = false, preserveHueLights = true, onProgress } = options
  const warnings: string[] = []
  let recordsImported = 0
  let filesImported = 0
  let tablesImported = 0
  
  // Timestamp for unique batch IDs
  const timestamp = new Date().toLocaleString()
  // Batch ID for soft-deleted existing data (for rollback restore)
  const existingDataBatchId = `Import Backup ${timestamp}`
  // Batch ID for newly imported data (for undo/delete imported)
  const importedDataBatchId = `Imported Data ${timestamp}`
  
  // Master ID mapping: oldId -> newId (for all records)
  const idMapping = new Map<string, string>()
  
  try {
    // Phase 1: Extract ZIP
    onProgress?.({
      phase: 'extracting',
      current: 0,
      total: 1,
      currentItem: 'Reading ZIP file...'
    })
    
    const zip = await JSZip.loadAsync(zipBlob)
    
    // Read and validate manifest
    const manifestFile = zip.file('manifest.json')
    if (!manifestFile) {
      return {
        success: false,
        error: 'Invalid export file: missing manifest.json',
        tablesImported: 0,
        recordsImported: 0,
        filesImported: 0,
        warnings: []
      }
    }
    
    const manifest: ExportManifest = JSON.parse(await manifestFile.async('string'))
    
    if (manifest.version > EXPORT_VERSION) {
      return {
        success: false,
        error: `Export version ${manifest.version} is newer than supported version ${EXPORT_VERSION}`,
        tablesImported: 0,
        recordsImported: 0,
        filesImported: 0,
        warnings: []
      }
    }
    
    // Read table data
    const dataFile = zip.file('data.json')
    if (!dataFile) {
      return {
        success: false,
        error: 'Invalid export file: missing data.json',
        tablesImported: 0,
        recordsImported: 0,
        filesImported: 0,
        warnings: []
      }
    }
    
    const tableData: Record<string, any[]> = JSON.parse(await dataFile.async('string'))
    
    // Phase 2: Clear existing data if requested (soft-delete for rollback)
    if (clearExisting) {
      onProgress?.({
        phase: 'importing',
        current: 0,
        total: 1,
        currentItem: 'Backing up existing data...'
      })
      
      // Deactivate scenes to avoid unique constraint
      try {
        const activeScenes = await provider.query<{ id: string }>('scenes', {
          filters: [{ column: 'is_active', operator: 'eq', value: true }]
        })
        for (const scene of activeScenes) {
          await provider.update('scenes', scene.id, { is_active: false })
        }
      } catch (e) {
        logger.warn('[MigrationService] Could not deactivate scenes:', e)
      }
      
      // Clear all existing data including storage files
      // We delete everything because we're importing fresh data with new UUIDs
      // The old data is soft-deleted (restorable) but storage files are removed
      // since imported data will have its own files from the ZIP
      await clearAllData(provider, existingDataBatchId, false, warnings)
    }
    
    // Phase 3: Detect user ID for storage path remapping
    const newUserId = provider.getCurrentUserId()
    let oldUserId: string | null = null
    
    for (const table of Object.values(TABLES)) {
      const records = tableData[table]
      if (records && records.length > 0 && records[0].user_id) {
        oldUserId = records[0].user_id
        break
      }
    }
    
    if (oldUserId && newUserId) {
      logger.info(`[MigrationService] Will remap storage paths: ${oldUserId} -> ${newUserId}`)
    }
    
    // Phase 3b: Build ID mapping for ALL records (generate fresh UUIDs)
    // This ensures we always insert as new records, never conflict with existing
    onProgress?.({
      phase: 'importing',
      current: 0,
      total: 1,
      currentItem: 'Generating new IDs...'
    })
    
    for (const table of Object.values(TABLES)) {
      const records = tableData[table]
      if (!records) continue
      
      for (const record of records) {
        if (record.id) {
          const newId = crypto.randomUUID()
          idMapping.set(record.id, newId)
        }
      }
    }
    
    logger.info(`[MigrationService] Generated ${idMapping.size} new IDs for import`)
    
    // Phase 3c: Build Hue light ID mapping if preserving hardware references
    let lightIdMapping: Map<string, string> = new Map()
    
    if (preserveHueLights && tableData[TABLES.LIGHTS]) {
      lightIdMapping = await buildHueLightMapping(provider, tableData[TABLES.LIGHTS])
      if (lightIdMapping.size > 0) {
        logger.info(`[MigrationService] Built light ID mapping for ${lightIdMapping.size} lights`)
      }
    }
    
    // Phase 4a: Import scenes first (two-pass for self-referential parent_id)
    const scenesData = tableData[TABLES.SCENES]
    if (scenesData && scenesData.length > 0) {
      onProgress?.({
        phase: 'importing',
        current: 0,
        total: 1,
        currentItem: `Importing ${TABLES.SCENES} (${scenesData.length} records)...`
      })
      
      const userId = provider.getCurrentUserId()
      let activeSceneNewId: string | null = null
      
      // First pass: insert scenes with new IDs, no parent_id, inactive
      for (const record of scenesData) {
        try {
          const oldId = record.id
          const newId = idMapping.get(oldId)!
          
          const mappedRecord = { ...record }
          mappedRecord.id = newId
          
          if (userId) {
            mappedRecord.user_id = userId
          }
          
          // Store parent_id for second pass
          const savedParentId = mappedRecord.parent_id
          delete mappedRecord.parent_id
          
          // Remap container_id if present
          if (mappedRecord.container_id && idMapping.has(mappedRecord.container_id)) {
            mappedRecord.container_id = idMapping.get(mappedRecord.container_id)
          }
          
          // Track active scene but import as inactive
          if (mappedRecord.is_active) {
            activeSceneNewId = newId
            mappedRecord.is_active = false
          }
          
          // Tag with import batch for potential undo
          mappedRecord.deletion_batch_id = importedDataBatchId
          
          // Clear soft-delete fields
          mappedRecord.deleted_at = null
          
          await provider.insert(TABLES.SCENES, mappedRecord)
          recordsImported++
          
          // Store for parent_id update
          if (savedParentId) {
            (record as any).__savedParentId = savedParentId
            (record as any).__newId = newId
          }
        } catch (error) {
          warnings.push(`Failed to import scene ${record.id}: ${error}`)
          logger.warn(`[MigrationService] Failed to import scene:`, error)
        }
      }
      
      // Second pass: update parent_id with remapped IDs
      for (const record of scenesData) {
        const savedParentId = (record as any).__savedParentId
        const newId = (record as any).__newId || idMapping.get(record.id)
        
        if (savedParentId && newId) {
          const newParentId = idMapping.get(savedParentId)
          if (newParentId) {
            try {
              await provider.update(TABLES.SCENES, newId, { parent_id: newParentId })
            } catch (error) {
              warnings.push(`Failed to set parent_id for scene: ${error}`)
            }
          }
        }
      }
      
      // Third pass: activate the scene that was originally active
      if (activeSceneNewId) {
        try {
          await provider.update(TABLES.SCENES, activeSceneNewId, { is_active: true })
        } catch (error) {
          warnings.push(`Failed to activate scene: ${error}`)
        }
      }
      
      tablesImported++
      logger.info(`[MigrationService] Imported ${scenesData.length} records into ${TABLES.SCENES}`)
    }
    
    // Phase 4b: Import remaining tables in dependency order
    const importOrder = getTableImportOrder()
    const totalTables = importOrder.length
    
    for (let i = 0; i < importOrder.length; i++) {
      const table = importOrder[i]
      const records = tableData[table]
      
      if (!records || records.length === 0) {
        continue
      }
      
      onProgress?.({
        phase: 'importing',
        current: i,
        total: totalTables,
        currentItem: `Importing ${table} (${records.length} records)...`
      })
      
      let tableRecordsImported = 0
      let tableErrors = 0
      
      for (const record of records) {
        try {
          const oldId = record.id
          const newId = idMapping.get(oldId)!
          
          let mappedRecord = { ...record }
          mappedRecord.id = newId
          
          // Apply Hue light ID mapping if needed (hardware-based matching)
          mappedRecord = applyIdMappings(table, mappedRecord, lightIdMapping)
          
          // Apply schema transformations
          mappedRecord = transformRecordForProvider(table, mappedRecord, provider.getMode())
          
          // Remap storage paths
          if (oldUserId && newUserId && oldUserId !== newUserId) {
            mappedRecord = remapStoragePaths(table, mappedRecord, oldUserId, newUserId)
          }
          
          // Update user_id to current user
          if (newUserId && 'user_id' in mappedRecord) {
            mappedRecord.user_id = newUserId
          }
          
          // Remap all foreign key columns using the ID mapping
          const fkColumns = FK_COLUMNS[table] || []
          let skipRecord = false
          for (const fkCol of fkColumns) {
            if (mappedRecord[fkCol]) {
              if (idMapping.has(mappedRecord[fkCol])) {
                mappedRecord[fkCol] = idMapping.get(mappedRecord[fkCol])
              } else {
                // FK value exists but can't be remapped - this is an orphaned record
                logger.warn(`[MigrationService] Cannot remap ${fkCol} for ${table} record ${oldId}: FK ${mappedRecord[fkCol]} not in idMapping`)
                warnings.push(`[${table}] Skipping record ${oldId}: orphaned ${fkCol}`)
                skipRecord = true
                break
              }
            }
          }
          
          if (skipRecord) {
            continue
          }
          
          // Special handling for display_layers.content_source (polymorphic field)
          // When layer_type is 'image', content_source is an image_library.id that needs remapping
          if (table === TABLES.DISPLAY_LAYERS && mappedRecord.layer_type === 'image' && mappedRecord.content_source) {
            if (idMapping.has(mappedRecord.content_source)) {
              mappedRecord.content_source = idMapping.get(mappedRecord.content_source)
            }
          }
          
          // Tag with import batch for potential undo
          mappedRecord.deletion_batch_id = importedDataBatchId
          
          // Clear soft-delete fields
          mappedRecord.deleted_at = null
          
          // Debug logging for display_layer_overrides RLS issues
          if (table === TABLES.DISPLAY_LAYER_OVERRIDES) {
            logger.info(`[MigrationService] Inserting display_layer_overrides:`, {
              id: mappedRecord.id,
              scene_display_config_id: mappedRecord.scene_display_config_id,
              parent_layer_id: mappedRecord.parent_layer_id,
              originalConfigId: record.scene_display_config_id,
              wasRemapped: record.scene_display_config_id !== mappedRecord.scene_display_config_id
            })
          }
          
          // Debug logging for scene_display_configs RLS issues
          if (table === TABLES.SCENE_DISPLAY_CONFIGS) {
            logger.info(`[MigrationService] Inserting scene_display_configs:`, {
              id: mappedRecord.id,
              scene_id: mappedRecord.scene_id,
              display_id: mappedRecord.display_id,
              originalSceneId: record.scene_id,
              wasRemapped: record.scene_id !== mappedRecord.scene_id
            })
          }
          
          await provider.insert(table, mappedRecord)
          recordsImported++
          tableRecordsImported++
        } catch (error) {
          tableErrors++
          if (tableErrors <= 3) {
            warnings.push(`[${table}] Record ${record.id}: ${error}`)
            logger.warn(`[MigrationService] Error importing ${table} record:`, error)
          }
        }
      }
      
      if (tableRecordsImported > 0) {
        tablesImported++
      }
      
      if (tableErrors > 3) {
        warnings.push(`[${table}] ... and ${tableErrors - 3} more errors`)
      }
      
      logger.info(`[MigrationService] Imported ${tableRecordsImported}/${records.length} records into ${table}`)
    }
    
    // Phase 5: Import files with remapped paths
    const filesFolder = zip.folder('files')
    if (filesFolder) {
      const buckets = Object.values(STORAGE_BUCKETS)
      
      for (const bucket of buckets) {
        const bucketFolder = filesFolder.folder(bucket)
        if (!bucketFolder) continue
        
        const files = Object.keys(zip.files)
          .filter(path => path.startsWith(`files/${bucket}/`) && !path.endsWith('.meta.json') && !path.endsWith('/'))
        
        for (const filePath of files) {
          onProgress?.({
            phase: 'files',
            current: filesImported,
            total: files.length,
            currentItem: filePath
          })
          
          try {
            const file = zip.file(filePath)
            if (!file) continue
            
            const rawBlob = await file.async('blob')
            
            const metaFile = zip.file(`${filePath}.meta.json`)
            let originalPath: string
            let contentType = 'application/octet-stream'
            
            if (metaFile) {
              const meta = JSON.parse(await metaFile.async('string'))
              originalPath = meta.originalPath
              contentType = meta.contentType || inferContentType(originalPath)
              
              // Remap user ID in path
              if (newUserId) {
                originalPath = originalPath.replace(/^[a-f0-9-]{36}\//, `${newUserId}/`)
              }
            } else {
              const fileName = filePath.split('/').pop()!
              originalPath = fileName.replace(/__/g, '/')
              contentType = inferContentType(originalPath)
            }
            
            const typedBlob = new Blob([rawBlob], { type: contentType })
            
            await provider.uploadFile(bucket, originalPath, typedBlob)
            filesImported++
          } catch (error) {
            const msg = `Failed to import file ${filePath}: ${error}`
            warnings.push(msg)
            logger.warn(`[MigrationService] ${msg}`)
          }
        }
      }
    }
    
    logger.info(`[MigrationService] Import complete: ${tablesImported} tables, ${recordsImported} records, ${filesImported} files`)
    
    // Import succeeded - the soft-deleted old data stays in trash for user to manage
    // User can restore it from the trash UI if needed, or empty trash to permanently delete
    logger.info(`[MigrationService] Old data is in trash (batch: "${existingDataBatchId}"), new data tagged with "${importedDataBatchId}"`)
    
    return {
      success: true,
      tablesImported,
      recordsImported,
      filesImported,
      warnings
    }
  } catch (error) {
    logger.error('[MigrationService] Import failed:', error)
    
    // Import failed - delete any partially imported data and restore old data
    if (clearExisting) {
      logger.info('[MigrationService] Rolling back failed import...')
      try {
        // Hard delete any imported records (they have the import batch ID)
        await hardDeleteByBatch(provider, importedDataBatchId)
        
        // Restore the soft-deleted original data
        await restoreAllData(provider, existingDataBatchId)
        warnings.push('Import failed - previous data has been restored')
      } catch (restoreError) {
        logger.error('[MigrationService] Failed to rollback after import failure:', restoreError)
        warnings.push(`Import failed and rollback also failed: ${restoreError}`)
      }
    }
    
    return {
      success: false,
      error: String(error),
      tablesImported,
      recordsImported,
      filesImported,
      warnings
    }
  }
}

/**
 * Helper to list all files in a bucket recursively
 */
async function listAllFiles(
  provider: DataProvider, 
  bucket: string, 
  _path: string
): Promise<FileMetadata[]> {
  try {
    if (provider.getMode() === 'standalone') {
      // For standalone, use the dedicated method to get all file paths
      const standaloneProvider = provider as any
      if (standaloneProvider.getAllFilePaths) {
        const paths = await standaloneProvider.getAllFilePaths(bucket)
        return paths.map((path: string) => ({
          path,
          size: 0, // Will be determined when we read the blob
          contentType: inferContentType(path), // Infer from file extension
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }))
      }
    }
    
    // For Supabase, use listFiles
    // Note: Supabase listFiles requires a path, so we list the user's folder
    const userId = provider.getCurrentUserId()
    const files = await provider.listFiles(bucket, userId || '')
    
    // Filter out directories (if any) and return actual files
    return files.filter(f => f.size > 0)
  } catch (error) {
    logger.warn(`[MigrationService] Failed to list files in ${bucket}:`, error)
    return []
  }
}

/**
 * Helper to get actual file blob from provider
 * 
 * For standalone mode, we need to access IndexedDB directly
 * For Supabase, we need to download via signed URL
 */
async function getFileBlob(
  provider: DataProvider,
  bucket: string,
  path: string
): Promise<Blob | null> {
  if (provider.getMode() === 'standalone') {
    // Access IndexedDB directly through a helper method
    // We need to expose this on the provider
    const standaloneProvider = provider as any
    if (standaloneProvider.getFileBlob) {
      return standaloneProvider.getFileBlob(bucket, path)
    }
    
    // Fallback: try to get via URL and fetch
    const url = await provider.getFileUrl(bucket, path)
    if (url) {
      try {
        const response = await fetch(url)
        return response.blob()
      } catch {
        return null
      }
    }
    return null
  } else {
    // Supabase: download via signed URL
    const url = await provider.getFileUrl(bucket, path, 60) // 60 second expiry
    if (url) {
      try {
        const response = await fetch(url)
        return response.blob()
      } catch {
        return null
      }
    }
    return null
  }
}

/**
 * Clear all data from the provider
 * @param preserveStorageFiles - If true, keep storage files (for soft delete scenarios where rollback is needed)
 * @param warnings - Optional array to collect warning messages
 */
async function clearAllData(provider: DataProvider, batchId?: string, preserveStorageFiles = false, warnings?: string[]): Promise<void> {
  // Tables that don't exist in Supabase but are in TABLES constant
  const skipTables = new Set([
    'image_library_folders' // Only exists in standalone IndexedDB
  ])
  
  // Generate a batch ID for this import operation if not provided
  const deletionBatchId = batchId || 'Import Backup'
  
  // Delete in reverse dependency order, plus SCENES at the end (self-referential)
  const deleteOrder = [...getTableImportOrder().reverse(), TABLES.SCENES]
  
  for (const table of deleteOrder) {
    if (skipTables.has(table)) {
      continue
    }
    
    try {
      const records = await provider.query<{ id: string }>(table)
      logger.info(`[MigrationService] Found ${records.length} records to delete from ${table}`)
      for (const record of records) {
        try {
          await provider.delete(table, record.id, deletionBatchId)
        } catch (deleteError) {
          logger.error(`[MigrationService] Failed to delete record ${record.id} from ${table}:`, deleteError)
          warnings?.push(`Failed to delete ${table} record ${record.id}: ${deleteError}`)
        }
      }
      if (records.length > 0) {
        logger.info(`[MigrationService] Cleared ${records.length} records from ${table} (batch: ${deletionBatchId})`)
      }
    } catch (error: any) {
      // Skip "table not found" errors gracefully
      if (error?.code === 'PGRST205' || error?.message?.includes('not found')) {
        logger.debug(`[MigrationService] Table ${table} not found, skipping`)
      } else {
        logger.warn(`[MigrationService] Failed to clear table ${table}:`, error)
        warnings?.push(`Failed to query ${table}: ${error}`)
      }
    }
  }
  
  // Only clear storage buckets if explicitly requested (not for import with rollback support)
  if (!preserveStorageFiles) {
    const buckets = Object.values(STORAGE_BUCKETS)
    for (const bucket of buckets) {
      try {
        const files = await provider.listFiles(bucket, '')
        for (const file of files) {
          await provider.deleteFile(bucket, file.path)
        }
        if (files.length > 0) {
          logger.info(`[MigrationService] Cleared ${files.length} files from ${bucket}`)
        }
      } catch (error) {
        logger.warn(`[MigrationService] Failed to clear bucket ${bucket}:`, error)
      }
    }
  } else {
    logger.info(`[MigrationService] Preserving storage files for rollback support`)
  }
}

/**
 * Restore all soft-deleted data from a specific import batch
 * Used to rollback a failed import
 */
async function restoreAllData(provider: DataProvider, batchId?: string): Promise<void> {
  const tables = Object.values(TABLES)
  
  for (const table of tables) {
    try {
      // Query only deleted records
      const filters = batchId 
        ? [{ column: 'deletion_batch_id', operator: 'eq' as const, value: batchId }]
        : [{ column: 'deleted_at', operator: 'is' as const, value: 'not_null' }]
      
      const deleted = await provider.query<{ id: string }>(table, { 
        includeDeleted: true,
        filters
      })
      
      for (const record of deleted) {
        await provider.restore(table, record.id)
      }
      
      if (deleted.length > 0) {
        logger.info(`[MigrationService] Restored ${deleted.length} records in ${table}`)
      }
    } catch (error: any) {
      // Skip errors gracefully
      if (error?.code !== 'PGRST205' && !error?.message?.includes('not found')) {
        logger.warn(`[MigrationService] Failed to restore table ${table}:`, error)
      }
    }
  }
}

/**
 * Permanently delete all soft-deleted data (empty trash)
 * Called after successful import to clean up old data
 */
async function permanentlyDeleteSoftDeleted(provider: DataProvider): Promise<void> {
  const tables = Object.values(TABLES)
  
  for (const table of tables) {
    try {
      // Query only deleted records
      const deleted = await provider.query<{ id: string }>(table, { 
        includeDeleted: true,
        filters: [{ column: 'deleted_at', operator: 'is', value: 'not_null' }]
      })
      
      for (const record of deleted) {
        await provider.hardDelete(table, record.id)
      }
      
      if (deleted.length > 0) {
        logger.info(`[MigrationService] Permanently deleted ${deleted.length} records from ${table}`)
      }
    } catch (error: any) {
      // Skip errors gracefully
      if (error?.code !== 'PGRST205' && !error?.message?.includes('not found')) {
        logger.warn(`[MigrationService] Failed to permanently delete from ${table}:`, error)
      }
    }
  }
}

/**
 * Hard delete all records with a specific deletion_batch_id
 * Used to roll back failed imports by deleting partially imported data
 */
async function hardDeleteByBatch(provider: DataProvider, batchId: string): Promise<void> {
  const tables = Object.values(TABLES)
  
  for (const table of tables) {
    try {
      // Query records with the specific batch ID (these are imported records, not soft-deleted)
      const records = await provider.query<{ id: string; deletion_batch_id?: string }>(table, { 
        includeDeleted: true,
        filters: [{ column: 'deletion_batch_id', operator: 'eq', value: batchId }]
      })
      
      for (const record of records) {
        await provider.hardDelete(table, record.id)
      }
      
      if (records.length > 0) {
        logger.info(`[MigrationService] Rolled back ${records.length} imported records from ${table}`)
      }
    } catch (error: any) {
      // Skip errors gracefully
      if (error?.code !== 'PGRST205' && !error?.message?.includes('not found')) {
        logger.warn(`[MigrationService] Failed to rollback imported records from ${table}:`, error)
      }
    }
  }
}

/**
 * Transform a record to match the target provider's schema
 * Handles differences between Standalone (IndexedDB) and Online (Supabase) schemas
 */
function transformRecordForProvider(
  table: string,
  record: any,
  targetMode: 'standalone' | 'online'
): any {
  const transformed = { ...record }
  
  // image_library: Supabase uses uploaded_at/updated_at, standalone might use created_at
  if (table === TABLES.IMAGE_LIBRARY) {
    if (targetMode === 'online') {
      // Convert standalone schema to Supabase schema
      if (transformed.created_at && !transformed.uploaded_at) {
        transformed.uploaded_at = transformed.created_at
        delete transformed.created_at
      }
    } else {
      // Convert Supabase schema to standalone schema
      if (transformed.uploaded_at && !transformed.created_at) {
        transformed.created_at = transformed.uploaded_at
        // Keep uploaded_at for compatibility
      }
    }
  }
  
  // Remove any undefined values that might cause issues
  for (const key of Object.keys(transformed)) {
    if (transformed[key] === undefined) {
      delete transformed[key]
    }
  }
  
  return transformed
}

/**
 * Remap storage paths from old user ID to new user ID
 * 
 * Storage paths typically follow the pattern: userId/subfolder/filename
 * We need to update these paths when importing data from a different user.
 */
function remapStoragePaths(
  table: string,
  record: any,
  oldUserId: string,
  newUserId: string
): any {
  const mapped = { ...record }
  
  // Helper to remap a single path
  const remapPath = (path: string | null | undefined): string | null | undefined => {
    if (!path || typeof path !== 'string') return path
    // Replace old user ID at the start of the path
    const pattern = new RegExp(`^${oldUserId}/`)
    if (pattern.test(path)) {
      return path.replace(pattern, `${newUserId}/`)
    }
    return path
  }
  
  // Tables with storage_path field
  if (table === TABLES.MUSIC_NODES || table === TABLES.AUDIO_LIBRARY_FILES) {
    if (mapped.storage_path) {
      mapped.storage_path = remapPath(mapped.storage_path)
    }
    if (mapped.cover_storage_path) {
      mapped.cover_storage_path = remapPath(mapped.cover_storage_path)
    }
  }
  
  // image_library
  if (table === TABLES.IMAGE_LIBRARY) {
    if (mapped.storage_path) {
      mapped.storage_path = remapPath(mapped.storage_path)
    }
  }
  
  // audio_library
  if (table === TABLES.AUDIO_LIBRARY) {
    if (mapped.file_path) {
      mapped.file_path = remapPath(mapped.file_path)
    }
  }
  
  // graph_containers
  if (table === TABLES.GRAPH_CONTAINERS) {
    if (mapped.background_image_path) {
      mapped.background_image_path = remapPath(mapped.background_image_path)
    }
  }
  
  // display_layers - content_source for image layers
  if (table === TABLES.DISPLAY_LAYERS) {
    if (mapped.layer_type === 'image' && mapped.content_source) {
      // content_source might be an image library ID or a direct path
      // If it looks like a path (contains /), remap it
      if (typeof mapped.content_source === 'string' && mapped.content_source.includes('/')) {
        mapped.content_source = remapPath(mapped.content_source)
      }
    }
  }
  
  return mapped
}

/**
 * Build mapping from imported light IDs to existing light IDs
 * Matches by bridge_id (Hue hardware ID) + light_id (Hue light number)
 */
async function buildHueLightMapping(
  provider: DataProvider,
  importedLights: any[]
): Promise<Map<string, string>> {
  const mapping = new Map<string, string>()
  
  try {
    // Get existing lights
    const existingLights = await provider.query<any>(TABLES.LIGHTS)
    
    // Create lookup by hardware identifiers
    const existingByHueId = new Map<string, string>()
    for (const light of existingLights) {
      // Key is bridge hardware ID + light number
      const key = `${light.hue_bridge_id}:${light.light_id}`
      existingByHueId.set(key, light.id)
    }
    
    // Map imported lights to existing
    for (const imported of importedLights) {
      const key = `${imported.hue_bridge_id}:${imported.light_id}`
      const existingId = existingByHueId.get(key)
      
      if (existingId && existingId !== imported.id) {
        // Same hardware light but different UUID - map old to new
        mapping.set(imported.id, existingId)
        logger.info(`[MigrationService] Mapped light ${imported.id} -> ${existingId} (${key})`)
      }
    }
  } catch (error) {
    logger.warn(`[MigrationService] Failed to build light mapping:`, error)
  }
  
  return mapping
}

/**
 * Apply ID mappings to a record
 */
function applyIdMappings(
  table: string,
  record: any,
  lightIdMapping: Map<string, string>
): any {
  const mapped = { ...record }
  
  // Apply light ID mappings where relevant
  if (lightIdMapping.size > 0) {
    // scene_light_configs.light_id
    if (table === TABLES.SCENE_LIGHT_CONFIGS && mapped.light_id) {
      const newId = lightIdMapping.get(mapped.light_id)
      if (newId) {
        mapped.light_id = newId
      }
    }
    
    // display_layer_overrides.light_override
    if (table === TABLES.DISPLAY_LAYER_OVERRIDES && mapped.light_override) {
      const newId = lightIdMapping.get(mapped.light_override)
      if (newId) {
        mapped.light_override = newId
      }
    }
    
    // Skip importing lights that have mappings (use existing instead)
    if (table === TABLES.LIGHTS && lightIdMapping.has(mapped.id)) {
      // Return the existing light ID - the upsert will update instead of insert
      mapped.id = lightIdMapping.get(mapped.id)
    }
  }
  
  return mapped
}

/**
 * Get tables in dependency order for import
 * 
 * Parent tables must be imported before child tables to satisfy foreign keys
 */
function getTableImportOrder(): string[] {
  return [
    // Level 1: No dependencies (or only user_id)
    // Note: SCENES imported separately in two passes to handle parent_id self-reference
    TABLES.DISPLAYS,         // Standalone
    TABLES.HUE_BRIDGES,      // Standalone
    // Note: IMAGE_LIBRARY_FOLDERS doesn't exist in Supabase, skip it
    TABLES.AUDIO_LIBRARY_FOLDERS,
    TABLES.GRAPH_CONTAINERS, // Standalone
    TABLES.SPOTIFY_INTEGRATIONS,
    TABLES.CHARACTERS,
    
    // Level 2: Depend on level 1
    TABLES.LIGHTS,           // Depends on hue_bridges
    TABLES.IMAGE_LIBRARY,    // Standalone (no folders in Supabase)
    TABLES.AUDIO_LIBRARY,    // Depends on folders
    TABLES.AUDIO_LIBRARY_FILES,
    TABLES.SCENE_DISPLAY_CONFIGS, // Depends on scenes + displays
    
    // Music nodes - playlist_nodes must come BEFORE music_nodes (FK dependency)
    TABLES.PLAYLIST_NODES,
    TABLES.SPOTIFY_NODES,
    TABLES.MUSIC_NODES,      // Depends on playlist_nodes
    
    // Level 3: Depend on level 2
    TABLES.DISPLAY_LAYERS,   // Depends on scene_display_configs
    TABLES.SCENE_MUSIC_CONFIGS,
    TABLES.SCENE_SPOTIFY_CONFIGS,
    TABLES.SCENE_PLAYLIST_CONFIGS,
    TABLES.SCENE_LIGHT_CONFIGS, // Depends on lights
    TABLES.HUE_SYNC_SESSIONS,
    
    // Level 4: Depend on level 3
    TABLES.DISPLAY_LAYER_OVERRIDES // Depends on display_layers
  ]
}

/**
 * Download a blob as a file (browser helper)
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Generate a default export filename with timestamp, user hint, and random word combo
 */
export function getExportFilename(userEmail?: string): string {
  const now = new Date()
  const date = now.toISOString().split('T')[0]
  const time = now.toTimeString().split(' ')[0].replace(/:/g, '-')
  
  // Extract username hint from email (first part before @, truncated)
  const userHint = userEmail 
    ? userEmail.split('@')[0].slice(0, 10).toLowerCase().replace(/[^a-z0-9]/g, '')
    : 'local'
  
  // Random word combo for uniqueness and easy identification
  const adjectives = ['swift', 'brave', 'calm', 'bold', 'keen', 'wise', 'warm', 'cool', 'fair', 'true']
  const nouns = ['fox', 'owl', 'wolf', 'bear', 'hawk', 'deer', 'lion', 'seal', 'crow', 'hare']
  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)]
  const noun = nouns[Math.floor(Math.random() * nouns.length)]
  
  return `ambi-${userHint}-${date}_${time}-${adjective}-${noun}.zip`
}

/**
 * Infer content type from file extension
 */
function inferContentType(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() || ''
  
  const mimeTypes: Record<string, string> = {
    // Images
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    'ico': 'image/x-icon',
    'bmp': 'image/bmp',
    
    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'ogg': 'audio/ogg',
    'flac': 'audio/flac',
    'aac': 'audio/aac',
    'm4a': 'audio/mp4',
    'webm': 'audio/webm',
    
    // Video
    'mp4': 'video/mp4',
    'avi': 'video/x-msvideo',
    'mov': 'video/quicktime',
    
    // Documents
    'pdf': 'application/pdf',
    'json': 'application/json',
    'txt': 'text/plain',
  }
  
  return mimeTypes[ext] || 'application/octet-stream'
}
